import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useWallet } from '../context/WalletContext';
import Web3 from "web3";

const API_BASE = "https://api.mbsmartweb.com/api";
const USDT_CONTRACT = "0x55d398326f99059fF775485246999027B3197955"; // BEP20 USDT
const PLATFORM_WALLET = "0xedb28Df79BF0D08Be7De9f267Cd336E1A77E99Fa"; // deposit wallet
const BSC_CHAIN_ID = 56;

// Minimal ERC20 ABI for USDT
const USDT_ABI = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "_to", type: "address" },
      { name: "_value", type: "uint256" },
    ],
    name: "transfer",
    outputs: [{ name: "success", type: "bool" }],
    type: "function",
  },
];

const AddFunds = () => {
  const {
    walletAddress,
    chainId,
    isConnected,
    connectWallet,
    disconnectWallet,
    provider,
  } = useWallet();

  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [txStatus, setTxStatus] = useState("");
  const [depositSuccess, setDepositSuccess] = useState(false);
  const [txDetails, setTxDetails] = useState(null);
  const [registeredAddress, setRegisteredAddress] = useState("");
  const [balance, setBalance] = useState("0");
  const [decimals, setDecimals] = useState(18);

  const txIntervalRef = useRef(null);
  const UserId = localStorage.getItem("UserId");
  const isCorrectNetwork = chainId === BSC_CHAIN_ID;

  const getWeb3 = () => (provider ? new Web3(provider) : null);

  // Fetch user's registered wallet address from backend
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!UserId) return;
      try {
        const response = await axios.post(`${API_BASE}/userdetails`, { userId: UserId });
        const addr = response.data?.Address;
        if (addr) {
          setRegisteredAddress(addr.toLowerCase());
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };
    fetchUserDetails();
  }, [UserId]);

  // Load USDT balance
  const loadBalance = async () => {
    try {
      const web3 = getWeb3();
      if (!web3 || !walletAddress) return;
      const contract = new web3.eth.Contract(USDT_ABI, USDT_CONTRACT);
      const dec = await contract.methods.decimals().call();
      setDecimals(Number(dec));
      const bal = await contract.methods.balanceOf(walletAddress).call();
      setBalance((bal / 10 ** dec).toFixed(4));
    } catch (e) {
      console.error("Error loading USDT balance:", e);
    }
  };

  useEffect(() => {
    if (isConnected && isCorrectNetwork && walletAddress) {
      loadBalance();
    }
  }, [walletAddress, chainId, isConnected, isCorrectNetwork]);

  // Switch to BSC Mainnet
  const switchToBSC = async () => {
    if (!provider) {
      toast.error("Wallet not connected");
      return false;
    }
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: "0x38" }],
      });
      toast.success("Switched to BSC Mainnet");
      return true;
    } catch (err) {
      if (err.code === 4902) {
        try {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: "0x38",
                chainName: "Binance Smart Chain Mainnet",
                nativeCurrency: {
                  name: "BNB",
                  symbol: "BNB",
                  decimals: 18,
                },
                rpcUrls: ["https://bsc-dataseed.binance.org/"],
                blockExplorerUrls: ["https://bscscan.com"],
              },
            ],
          });
          toast.success("BSC network added");
          return true;
        } catch (addErr) {
          toast.error("Failed to add BSC network");
          return false;
        }
      }
      toast.error("Network switch rejected");
      return false;
    }
  };

  // Check transaction status
  const checkTxStatus = async (hash) => {
    if (!hash) return null;
    try {
      const web3 = getWeb3();
      if (!web3) return null;
      const receipt = await web3.eth.getTransactionReceipt(hash);
      if (receipt) {
        const status = receipt.status ? "success" : "failed";
        const block = await web3.eth.getBlock(receipt.blockNumber);
        const details = {
          hash: receipt.transactionHash,
          status,
          blockNumber: receipt.blockNumber,
          gasUsed: receipt.gasUsed.toString(),
          timestamp: block ? block.timestamp : Math.floor(Date.now() / 1000),
        };
        setTxDetails(details);
        setTxStatus(status);
        return { status, details };
      }
      return { status: "pending", details: null };
    } catch (error) {
      console.error("Error checking transaction:", error);
      return { status: "pending", details: null };
    }
  };

  // Monitor transaction
  const monitorTransaction = (hash) => {
    if (!hash || txIntervalRef.current) return;
    let attempts = 0;
    const maxAttempts = 60;
    txIntervalRef.current = setInterval(async () => {
      attempts++;
      const result = await checkTxStatus(hash);
      if (result?.status === "success") {
        clearInterval(txIntervalRef.current);
        txIntervalRef.current = null;
        setTxStatus("success");
        setDepositSuccess(true);
        toast.success("✅ Transaction Confirmed");
        await loadBalance();
      }
      if (result?.status === "failed") {
        clearInterval(txIntervalRef.current);
        txIntervalRef.current = null;
        setTxStatus("failed");
        toast.error("❌ Transaction Failed");
      }
      if (attempts >= maxAttempts) {
        clearInterval(txIntervalRef.current);
        txIntervalRef.current = null;
        setTxStatus("timeout");
        toast.warning("⏱️ Transaction taking too long");
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (txIntervalRef.current) {
        clearInterval(txIntervalRef.current);
        txIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (txHash && txStatus === "pending") {
      monitorTransaction(txHash);
    }
  }, [txHash, txStatus]);

  // Parse transaction errors
  const parseTxError = (e) => {
    const msg = e?.data?.message || e?.error?.message || e?.message || "Transaction failed";
    if (/rejected|denied|cancel/i.test(msg)) return "Transaction rejected by user";
    if (/insufficient/i.test(msg)) return "Insufficient BNB for gas";
    if (/revert/i.test(msg)) return "Smart contract reverted";
    if (/network/i.test(msg)) return "Wrong network selected";
    return msg;
  };

  // Handle deposit
 // Handle deposit
const handleDeposit = async () => {
  if (loading || txStatus === "pending") {
    toast.warning("Transaction already in progress");
    return;
  }
  if (!amount || isNaN(amount) || Number(amount) <= 0) {
    toast.error("Enter a valid amount");
    return;
  }
  if (Number(amount) < 10) {
    toast.error("Minimum deposit amount is $10");
    return;
  }
  if (!isConnected || !walletAddress) {
    toast.error("Wallet not connected");
    return;
  }
  if (!registeredAddress) {
    toast.error("No registered wallet address found. Please update your profile.");
    return;
  }
  if (walletAddress.toLowerCase() !== registeredAddress.toLowerCase()) {
    toast.error("Connected wallet does not match your registered address.");
    return;
  }
  if (!isCorrectNetwork) {
    const switched = await switchToBSC();
    if (!switched) return;
  }

  // Check USDT balance
  try {
    const web3 = getWeb3();
    if (!web3) {
      toast.error("Web3 provider not available");
      return;
    }
    const contract = new web3.eth.Contract(USDT_ABI, USDT_CONTRACT);
    const rawAmount = web3.utils.toWei(amount.toString(), "ether");
    const userBalance = await contract.methods.balanceOf(walletAddress).call();
    
    // Fix: Use Number conversion instead of BigInt
    const userBalanceNum = parseFloat(userBalance);
    const rawAmountNum = parseFloat(rawAmount);
    
    if (userBalanceNum < rawAmountNum) {
      toast.error("Insufficient USDT balance");
      return;
    }
  } catch (err) {
    console.error("Balance check error:", err);
    toast.error("Failed to verify balance");
    return;
  }

  setLoading(true);
  setTxStatus("");
  setTxDetails(null);
  setDepositSuccess(false);

  try {
    const web3 = getWeb3();
    if (!web3) throw new Error("Web3 not available");
    const contract = new web3.eth.Contract(USDT_ABI, USDT_CONTRACT);
    const rawAmount = web3.utils.toWei(amount.toString(), "ether");

    contract.methods
      .transfer(PLATFORM_WALLET, rawAmount)
      .send({ from: walletAddress })
      .on("transactionHash", (hash) => {
        setTxHash(hash);
        setTxStatus("pending");
        setLoading(false);
        toast.info(`Transaction Submitted: ${hash.slice(0, 12)}...`);
      })
      .on("receipt", async (receipt) => {
        if (txIntervalRef.current) {
          clearInterval(txIntervalRef.current);
          txIntervalRef.current = null;
        }
        setTxStatus("success");
        setDepositSuccess(true);
        setTxDetails({
          hash: receipt.transactionHash,
          blockNumber: receipt.blockNumber,
          gasUsed: receipt.gasUsed.toString(),
          timestamp: Math.floor(Date.now() / 1000),
        });
        toast.success("✅ Transaction Confirmed");
        await loadBalance();
      })
      .on("error", (err) => {
        setLoading(false);
        const msg = parseTxError(err);
        setTxStatus("failed");
        toast.error(`❌ ${msg}`);
      });
  } catch (err) {
    console.error("Deposit exception:", err);
    toast.error(parseTxError(err));
    setLoading(false);
  }
};

  const copyAddress = () => {
    navigator.clipboard.writeText(PLATFORM_WALLET);
    toast.success("Address copied to clipboard!");
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={6000} />
      <div className="add-funds-page">
        <div className="page-header">
          <h1>Add Funds</h1>
          <p>Deposit USDT (BEP20) to your trading account</p>
        </div>

        {/* Success Overlay */}
        {depositSuccess && txStatus === "success" && (
          <div className="success-overlay">
            <div className="success-card">
              <div className="success-icon">✅</div>
              <h3>Deposit Successful!</h3>
              <p>{amount} USDT has been deposited to your account</p>
              {txDetails && (
                <div className="tx-details">
                  <div className="tx-detail">
                    <span className="detail-label">Block:</span>
                    <span className="detail-value">#{txDetails.blockNumber}</span>
                  </div>
                  <div className="tx-detail">
                    <span className="detail-label">Gas Used:</span>
                    <span className="detail-value">{txDetails.gasUsed}</span>
                  </div>
                </div>
              )}
              {txHash && (
                <a
                  href={`https://bscscan.com/tx/${txHash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="view-tx-link"
                >
                  View on BscScan ↗
                </a>
              )}
              <button
                className="close-success-btn"
                onClick={() => setDepositSuccess(false)}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        <div className="funds-container">
          {/* Wallet Connection Section */}
          <div className="wallet-section">
            {!isConnected ? (
              <button className="connect-wallet-btn" onClick={connectWallet}>
                Connect Wallet
              </button>
            ) : (
              <div className="wallet-info-card">
                <div className="wallet-header">
                  <div className="wallet-status-indicator connected"></div>
                  <span className="wallet-address">
                    {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                  </span>
                  <button className="disconnect-btn" onClick={disconnectWallet}>
                    Disconnect
                  </button>
                </div>
                <div className="wallet-balance-row">
                  <span>USDT Balance:</span>
                  <strong>{balance} USDT</strong>
                </div>
                {registeredAddress && registeredAddress !== walletAddress.toLowerCase() && (
                  <div className="wallet-warning" style={{ color: "#dc2626", marginTop: "8px" }}>
                    ⚠️ Connected wallet does not match your registered address.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="deposit-form">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="form-group">
                <label>Amount (USDT)</label>
                <div className="input-wrapper">
                  <i className="fa fa-dollar"></i>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount (Min: 10 USDT)"
                    required
                    min="10"
                    step="1"
                    disabled={loading || txStatus === "pending"}
                  />
                </div>
              </div>

              {/* Platform Address Display */}
              <div className="address-display">
                <label>Deposit Address</label>
                <div className="address-box">
                  <input type="text" value={PLATFORM_WALLET} readOnly />
                  <button type="button" className="copy-btn" onClick={copyAddress}>
                    Copy
                  </button>
                </div>
                <p className="note">Minimum deposit: $10 USDT • Network: BEP20</p>
              </div>

              {/* Transaction Status */}
              {txHash && (
                <div className="transaction-status-card">
                  <div className={`status-indicator ${txStatus}`}>
                    {txStatus === "pending" && (
                      <>
                        <span className="spinner-small"></span>
                        <span>Pending Confirmation...</span>
                      </>
                    )}
                    {txStatus === "success" && (
                      <>
                        <span>✅</span>
                        <span>Confirmed Successfully</span>
                      </>
                    )}
                    {txStatus === "failed" && (
                      <>
                        <span>❌</span>
                        <span>Transaction Failed</span>
                      </>
                    )}
                  </div>
                  {txHash && (
                    <div className="tx-hash">
                      <span>Hash: {txHash.slice(0, 20)}...{txHash.slice(-20)}</span>
                    </div>
                  )}
                </div>
              )}

              <button 
                type="button" 
                className="btn-primary" 
                onClick={handleDeposit}
                disabled={
                  loading ||
                  !isConnected ||
                  !amount ||
                  Number(amount) < 10 ||
                  txStatus === "pending" ||
                  (registeredAddress && registeredAddress !== walletAddress?.toLowerCase())
                }
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> SENDING...
                  </>
                ) : txStatus === "pending" ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> PROCESSING...
                  </>
                ) : txStatus === "success" ? (
                  "✓ DEPOSIT SUCCESSFUL"
                ) : (
                  <>
                    <i className="fa fa-plus-circle"></i> DEPOSIT {amount || "0"} USDT
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Info Section */}
          <div className="info-section">
            <div className="info-item">
              <span className="info-icon">⚠️</span>
              <span>Ensure you have enough BNB for gas fees</span>
            </div>
            <div className="info-item">
              <span className="info-icon">🔗</span>
              <span>Must be on BSC Mainnet (Chain ID: 56)</span>
            </div>
            <div className="info-item">
              <span className="info-icon">⏱️</span>
              <span>Transactions may take a few minutes to confirm</span>
            </div>
            <div className="info-item">
              <span className="info-icon">🔍</span>
              <span>Send only USDT (BEP20) to this address</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddFunds;